import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { ObservablePrimitive } from '@legendapp/state';
import { useObservable } from '@legendapp/state/react';
import {
  Alert,
  Card,
  InputAdornment,
  Link,
  LinearProgress,
  ListItemText,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { FormSubmitRow } from 'components/common/form/submit-row';
import { MetadataItem } from 'components/common/metadata';
import {
  ADMIN_RESULT_COUNTS,
  PhoneNumberPicker,
  PhoneSearchCriteria,
} from 'components/nouns/phone/number-picker';
import { AudioPreviewButton } from 'components/views/audio/player';
import { IvrFilesQuery, IvrFoldersQuery } from 'components/views/campaign/vendors/ivrs.graphql';
import { FormAutocomplete, FormTextField } from 'components/views/forms/form-text-field';
import { RadioForm } from 'components/views/forms/radio-form';
import { useSnackbar } from 'providers/snackbar';
import { FC, FormEvent, useEffect, useState } from 'react';
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form';
import { Link as RouterLink, useParams } from 'react-router';
import routes from 'routes';
import {
  CampaignVendors_campaign,
  CampaignVendors_campaign_agentPhones,
} from 'types/apollo/CampaignVendors';
import {
  IvrFiles,
  IvrFiles_getIvrFiles,
  IvrFiles_getIvrFiles_files,
  IvrFilesVariables,
} from 'types/apollo/IvrFiles';
import { IvrFolders } from 'types/apollo/IvrFolders';
import { ProvisionTelnyx, ProvisionTelnyxVariables } from 'types/apollo/ProvisionTelnyx';
import { TelnyxSearch, TelnyxSearchVariables } from 'types/apollo/TelnyxSearch';
import { formatDate } from 'utils/date';
import {
  DEFAULT_TELNYX_VOLUME,
  findExistingAgentPhone,
  findOtherVendorPhones,
  findProvisionedIvrFolder,
  findTelnyxVolume,
} from './provision-state';
import { ProvisionTelnyxMutation, TelnyxQuery } from './telnyx.graphql';

interface VendorFormData {
  phone: string;
  ivrFolder: string | null;
  ivrFolderOptions: string[];
  volume: number;
}

interface ProvisionVendorFormData {
  phone: string;
  vendor: '' | 'Telnyx' | 'Twilio';
}

export const IvrRow = ({
  file,
  currentlyPlayingUrl,
}: {
  file: IvrFiles_getIvrFiles_files;
  currentlyPlayingUrl: ObservablePrimitive<string>;
}) => (
  <Stack direction="row" alignItems="center">
    <AudioPreviewButton url={file.url} currentlyPlayingUrl={currentlyPlayingUrl} showSeekBar />
    <ListItemText
      sx={{ marginLeft: 1 }}
      primary={file.name}
      secondary={`Modified: ${formatDate(file.updatedAt)}`}
    />
  </Stack>
);

interface TelnyxRadioProps {
  agentPhones: CampaignVendors_campaign_agentPhones[];
  loadingFiles: boolean;
  onLoadingFilesChange: (loading: boolean) => void;
  provisionedIvrFolder: string | null;
}

const TelnyxRadio: FC<TelnyxRadioProps> = ({
  agentPhones,
  loadingFiles,
  onLoadingFilesChange,
  provisionedIvrFolder,
}) => {
  const { id: companyId } = useParams<{ id: string }>();
  // The picker is deliberately closed: a name typed here would point the campaign at an S3 path
  // with no audio in it. Folders are made on the IVRs page, so say so rather than just refusing.
  const folderHint = companyId ? (
    <>
      Existing folders only. To add one,{' '}
      <Link component={RouterLink} to={routes.company.ivrsAbs.resolve({ id: companyId })}>
        create it on the IVRs page
      </Link>
      .
    </>
  ) : (
    "Existing folders only. Create new ones on the company's IVRs page."
  );
  const existingAgentPhone = findExistingAgentPhone(agentPhones);
  const otherVendorPhones = findOtherVendorPhones(agentPhones);
  const form = useFormContext<VendorFormData>();
  const [files, setFiles] = useState<IvrFiles_getIvrFiles | null>(null);
  const currentlyPlayingUrl = useObservable<string>();
  const client = useApolloClient();
  const { openSnackbar } = useSnackbar();

  const searchPhones = async (criteria: PhoneSearchCriteria) => {
    const { data } = await client.query<TelnyxSearch, TelnyxSearchVariables>({
      query: TelnyxQuery,
      variables: criteria,
      fetchPolicy: 'no-cache',
    });
    return data.telnyxSearchPhones;
  };

  const { refetch: searchFiles } = useQuery<IvrFiles, IvrFilesVariables>(IvrFilesQuery, {
    skip: true,
    onCompleted: (d) => {
      if (d?.getIvrFiles) {
        setFiles(d.getIvrFiles);
      }
    },
  });

  const ivrFolder = form.watch('ivrFolder');
  const ivrFolderOptions = form.watch('ivrFolderOptions');

  // Keyed on both because the two queries land in either order. Left undirtied: showing what
  // was provisioned should not arm the Provision button.
  useEffect(() => {
    if (!provisionedIvrFolder || form.getValues('ivrFolder')) {
      return;
    }
    if (ivrFolderOptions?.includes(provisionedIvrFolder)) {
      form.setValue('ivrFolder', provisionedIvrFolder);
    }
  }, [provisionedIvrFolder, ivrFolderOptions]);

  useEffect(() => {
    if (!ivrFolder || !form.getValues('ivrFolderOptions')?.includes(ivrFolder)) {
      return;
    }
    // Drop the previous folder's listing: provisioning wires up whatever the folder holds, so
    // showing one folder's files under another folder's name invites provisioning the wrong audio.
    setFiles(null);
    onLoadingFilesChange(true);
    currentlyPlayingUrl.set('none');
    searchFiles({ path: ivrFolder })
      .catch(() =>
        openSnackbar({ message: `Could not load files in ${ivrFolder}`, severity: 'error' }),
      )
      .finally(() => onLoadingFilesChange(false));
  }, [ivrFolder]);

  return (
    <Stack direction="column">
      {/* A picker here would be dead: provisioning returns 'existing' and discards the selection
          when the campaign has a number. Buying a second one is provision/telnyx/phone. */}
      {existingAgentPhone ? (
        <MetadataItem label="Agent phone" value={existingAgentPhone} />
      ) : (
        <Controller
          control={form.control}
          name="phone"
          render={({ field }) => (
            <PhoneNumberPicker
              value={field.value}
              onChange={field.onChange}
              onSearch={searchPhones}
              showCost
              resultCounts={ADMIN_RESULT_COUNTS}
            />
          )}
        />
      )}
      {/* Context, not a control: provisioning here is Telnyx-only, so these are shown to explain
          why the campaign has a number and the Telnyx picker is still offered. */}
      {otherVendorPhones.map((p) => (
        <MetadataItem key={p.phone} label={`${p.vendor} agent phone`} value={p.phone} />
      ))}
      <Stack direction="row">
        <FormAutocomplete
          form={form}
          name="ivrFolder"
          optionsName="ivrFolderOptions"
          label="IVR folder name in AWS"
          freeSolo={false}
          helperText={folderHint}
          sx={{ width: 500 }}
        />
        <FormTextField
          form={form}
          name="volume"
          sx={{ width: 100, marginLeft: 2 }}
          type="number"
          inputProps={{
            min: 0,
            max: 100,
            inputMode: 'numeric',
            pattern: '[0-9]*',
          }}
          InputProps={{
            endAdornment: <InputAdornment position="end">%</InputAdornment>,
          }}
        />
      </Stack>

      {/* Provisioning saves whatever the volume field holds and the wizard leaves every campaign
          at 0, so an unremarked zero ships a fully provisioned campaign that receives nothing. */}
      {Number(form.watch('volume')) === 0 && (
        <Alert severity="error" sx={{ marginTop: 2 }}>
          Volume is <strong>0%</strong> — provisioning now will leave this campaign live but
          receiving no traffic. Set it to 100% unless you are deliberately holding it back.
        </Alert>
      )}

      {loadingFiles ? (
        <div>
          <Typography>
            <Typography component="span">Loading files in </Typography>
            <Typography component="span" fontWeight="bold">
              {ivrFolder}
            </Typography>
          </Typography>
          <LinearProgress sx={{ height: '2px', mt: 1 }} />
        </div>
      ) : (
        files && (
          <div>
            <Typography>
              <Typography component="span">Files in </Typography>
              <Typography component="span" fontWeight="bold">
                {files.name}
              </Typography>
            </Typography>
            {files.files.length === 0 ? (
              <Typography color="text.secondary">No audio files in this folder</Typography>
            ) : (
              files.files.map((f) => (
                <IvrRow key={f.url} file={f} currentlyPlayingUrl={currentlyPlayingUrl} />
              ))
            )}
          </div>
        )
      )}
    </Stack>
  );
};

interface ProvisionCardProps {
  campaign: Omit<CampaignVendors_campaign, '__typename'>;
  loading: boolean;
  onProvisioned: () => void;
}

export const ProvisionCard: FC<ProvisionCardProps> = ({ campaign, loading, onProvisioned }) => {
  const { openSnackbar } = useSnackbar();
  const [loadingIvrFiles, setLoadingIvrFiles] = useState(false);

  const telnyxForm = useForm<VendorFormData>({
    defaultValues: {
      phone: '',
      ivrFolder: null,
      ivrFolderOptions: [],
      volume: DEFAULT_TELNYX_VOLUME,
    },
    mode: 'all',
  });

  const vendorForm = useForm<ProvisionVendorFormData>({
    defaultValues: {
      vendor: 'Telnyx',
    },
  });

  const { loading: loadingIvrFolders } = useQuery<IvrFolders>(IvrFoldersQuery, {
    onCompleted: (d) => {
      if (d?.getIvrFolders) {
        telnyxForm.setValue('ivrFolderOptions', d.getIvrFolders);
      }
    },
  });

  // Keyed on the value, not the array: Apollo hands back a new array on every refetch, which
  // would otherwise overwrite an edit the admin is part-way through.
  const campaignVolume = findTelnyxVolume(campaign.vendorAccounts);
  useEffect(() => {
    telnyxForm.setValue('volume', campaignVolume);
  }, [campaignVolume]);

  const [provisionTelnyx, { loading: provisioning }] = useMutation<
    ProvisionTelnyx,
    ProvisionTelnyxVariables
  >(ProvisionTelnyxMutation);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    const ivrFolder = telnyxForm.getValues('ivrFolder');
    const agentPhoneNumber = telnyxForm.getValues('phone');
    // The IVR folder is the one thing provisioning cannot do without.
    if (!ivrFolder) {
      openSnackbar({ message: 'Select an IVR folder before provisioning', severity: 'error' });
      return;
    }

    // No number is the common case: the wizard's phone step buys one, so campaigns arrive
    // holding a number and missing the shared Twilio phones, the IVR and a non-zero volume.
    try {
      await provisionTelnyx({
        variables: {
          provisionTelnyx: {
            campaignId: campaign.id,
            ivrFolder,
            agentPhoneNumber: agentPhoneNumber || null,
            volume: Number(telnyxForm.getValues('volume')),
          },
        },
      });
      openSnackbar({ message: 'Telnyx provisioned', severity: 'success' });
    } catch (err) {
      // Provisioning half-fails in practice (a vendor rejecting A2P, a bad IVR folder), and the
      // generic message gave the admin nothing to act on.
      const reason = err instanceof Error ? err.message : '';
      openSnackbar({
        message: reason ? `Failed to provision Telnyx: ${reason}` : 'Failed to provision Telnyx',
        severity: 'error',
      });
    }

    onProvisioned();
  };

  return (
    <Card variant="outlined" sx={{ marginTop: 2, padding: 2 }}>
      <Typography variant="h4" component="h1">
        Provision
      </Typography>
      {/* Every field is query-seeded, so rendering early shows defaults about to be replaced
          — on the volume field that reads as a real 100%. */}
      {(loading && !campaign.id) || loadingIvrFolders ? (
        <LinearProgress sx={{ height: '2px', mt: 1 }} />
      ) : (
        <form onSubmit={handleSubmit}>
          <FormProvider {...telnyxForm}>
            <Controller
              control={vendorForm.control}
              name="vendor"
              rules={{
                required: true,
              }}
              render={({ field }) => (
                <RadioGroup {...field}>
                  <RadioForm selectedValue={vendorForm.watch('vendor')} label="Telnyx">
                    <TelnyxRadio
                      agentPhones={campaign.agentPhones}
                      loadingFiles={loadingIvrFiles}
                      onLoadingFilesChange={setLoadingIvrFiles}
                      provisionedIvrFolder={findProvisionedIvrFolder(campaign.ivrAudioUrls)}
                    />
                  </RadioForm>
                </RadioGroup>
              )}
            />
            {/* The folder is what provisioning acts on, so the button waits until its
                contents are on screen and the admin can see what they are wiring up. */}
            <FormSubmitRow
              sx={{ marginTop: 2 }}
              submitText="Provision"
              loading={provisioning}
              disabled={loadingIvrFiles}
            />
          </FormProvider>
        </form>
      )}
    </Card>
  );
};
